import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useEffect } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import Navigation from "./components/Navigation";
import Index from "./pages/Index";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import NotFound from "./pages/NotFound";
import FAQPage from "./pages/FAQ";
import BlogPost from "./pages/BlogPost";
import Blogs from "./pages/Blogs";

// Admin imports
import Login from "./pages/admin/Login";
import Dashboard from "./pages/admin/Dashboard";
import AdminBlogs from "./pages/admin/Blogs";
import CreateBlog from "./pages/admin/CreateBlog";
import EditBlog from "./pages/admin/EditBlog";
import CreateNewsletter from "./pages/admin/CreateNewsletter";
import Subscribers from "./pages/admin/Subscribers";
import Unsubscribe from "./pages/Unsubscribe";
import ProtectedRoute from "./routes/ProtectedRoute";
import Contacts from "./pages/admin/Contacts";
import NewsletterImages from "./pages/admin/NewsletterImages";

const queryClient = new QueryClient();

// Navigation wrapper component
const NavigationWrapper = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  if (isAdminRoute) return null;
  return <Navigation />;
};

function App() {
  // Disable right-click context menu on the entire application (except for blog content)
  useEffect(() => {
    const handleContextMenu = (e) => {
      // Allow right-click on blog content for text selection and copying
      const target = e.target;
      const isBlogContent = target.closest('.blog-content') || 
                           target.closest('[data-allow-select]') ||
                           window.location.pathname.includes('/blog/');
      
      // Allow text selection for blog posts
      if (isBlogContent) {
        return true;
      }
      
      e.preventDefault();
      return false;
    };

    // Add event listener to disable right-click (except for blog content)
    document.addEventListener("contextmenu", handleContextMenu);

    // Cleanup function to remove event listener
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
      <Router>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <NavigationWrapper />
            <main>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Index />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/blogs" element={<Blogs />} />
                <Route path="/blog/:id" element={<BlogPost />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />

                {/* Admin routes */}
                <Route path="/admin/login" element={<Login />} />
                <Route element={<ProtectedRoute />}>
                  <Route path="/admin" element={<Dashboard />}>
                    <Route index element={<Navigate to="blogs" replace />} />
                    <Route path="blogs" element={<AdminBlogs />} />
                    <Route path="blogs/create" element={<CreateBlog />} />
                    <Route path="blogs/:id/edit" element={<EditBlog />} />
                    <Route path="newsletters" element={<CreateNewsletter />} />
                    <Route path="subscribers" element={<Subscribers />} />
                    <Route path="contacts" element={<Contacts />} />
                    <Route path="media" element={<NewsletterImages />} />
                  </Route>
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
          </TooltipProvider>
        </QueryClientProvider>
      </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
